<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import {
  EyeIcon,
  ExternalIcon,
  GlobeIcon,
  // EngineerIcon,
  RefreshIcon,
  // FileExportIcon,
  // IndianRupeeIcon,
} from '@/icons'
// ✅ Import from your enhanced badgeUtils
import {
  getBadgeStatus,
  getWorkflowLabel
} from '@/utils/badgeUtils'
import router from '@/router'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
// import StatsCards from '@/components/cards/StatsCards.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import EngineerAssignmentModal from '../Modals/EngineerAssignmentModal.vue'
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue'
import MobileIcon from '@/icons/MobileIcon.vue'
import { notificationService } from '@/services/notificationService'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// Define interface for won tenders with agent info
type WonTenderResponse = Tender & {
  assignedAgent?: string
  assignedAgentName?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
  wonAt?: string
  winningAmount?: number
  awardNumber?: string
  contractStartDate?: string
  contractEndDate?: string
  assignedToEngineer?: string
  assignedEngineerName?: string
  engineerStatus?: string
  engineerAssignedAt?: string
  engineerNotes?: string
}

const tenders = ref<WonTenderResponse[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Add engineer assignment modal states
const isEngineerAssignmentModalOpen = ref(false)
const selectedTenderForEngineerAssignment = ref<WonTenderResponse | null>(null)

// Add ref for the engineer assignment modal
const engineerAssignmentModalRef = ref<InstanceType<typeof EngineerAssignmentModal> | null>(null)

// Statistics
// const wonCount = computed(() => tenders.value.length)
const totalWinningAmount = computed(() =>
  tenders.value.reduce(
    (sum, tender) => sum + (tender.winningAmount || tender.estimatedCost || 0),
    0,
  ),
)

// Format number for display
const formatNumber = (num: number): string => {
  if (!num) return '0'
  if (num >= 10000000) {
    return (num / 10000000).toFixed(2) + ' Cr'
  } else if (num >= 100000) {
    return (num / 100000).toFixed(2) + ' L'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(2) + ' K'
  }
  return num.toLocaleString('en-IN')
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Fetch won tenders from API
const fetchWonTenders = async () => {
  try {
    loading.value = true
    error.value = null

    const response = await api.get('/api/tenders/won')

    if (response.data.success) {
      tenders.value = response.data.tenders.map((tender: WonTenderResponse) => ({
        ...tender,
        assignedAgentName:
          tender.assignedAgentName ||
          tender.assignedAgent?.name ||
          tender.assignedAgent ||
          'Unknown Agent',
        assignedAgentEmail: tender.assignedAgentEmail || tender.assignedAgent?.email || '',
        assignedAgentPhone: tender.assignedAgentPhone || tender.assignedAgent?.phone || '',
      }))
      console.log(`✅ Loaded ${tenders.value.length} won tenders`, tenders.value)
    } else {
      error.value = response.data.error || 'Failed to load won tenders'
      notificationService.error('Failed to load won tenders: ' + response.data.error)
    }
  } catch (err: unknown) {
    console.error('Error fetching won tenders:', err)
    error.value = getAxiosErrorMessage(err as AxiosError)
    notificationService.error(
      'Failed to load won tenders: ' + getAxiosErrorMessage(err as AxiosError),
    )
  } finally {
    loading.value = false
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm) ||
      tender.assignedAgentName?.toLowerCase().includes(searchTerm) ||
      tender.awardNumber?.toLowerCase().includes(searchTerm) ||
      tender.assignedEngineerName?.toLowerCase().includes(searchTerm),
  )
})

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'won-tenders' },
  })
}

// Clear filters
const clearFilters = () => {
  searchFilter.value = ''
  notificationService.info('Filters cleared')
}

// Refresh data
const refreshData = async () => {
  notificationService.info('Refreshing won tenders...')
  await fetchWonTenders()
}

// Engineer assignment functions
// const openEngineerAssignmentModal = async (tender: WonTenderResponse) => {
//   selectedTenderForEngineerAssignment.value = tender
//   isEngineerAssignmentModalOpen.value = true
//   await nextTick()
// }

const handleEngineerAssignment = async () => {
  if (!engineerAssignmentModalRef.value) {
    console.error('Engineer assignment modal ref is not available')
    notificationService.error('Engineer assignment modal is not available')
    return
  }

  const isValid = engineerAssignmentModalRef.value.submitForm()

  if (isValid) {
    console.log('Form is valid, assignment will be triggered via emit')
  } else {
    console.log('Form validation failed')
    notificationService.error('Please fill in all required fields')
  }
}

const handleEngineerAssignmentData = async (assignmentData: {
  tenderId: string
  engineerId: string
  engineerName: string
  notes: string
}) => {
  console.log('📤 Assigning tender to engineer - Sending data:', assignmentData)

  try {
    const requestData = {
      tenderId: assignmentData.tenderId,
      engineerId: assignmentData.engineerId,
      engineerName: assignmentData.engineerName,
      notes: assignmentData.notes,
    }

    console.log('📝 Sending to API:', requestData)

    const response = await api.put('/api/tenders/assign-engineer', requestData)

    console.log('✅ API Response:', {
      success: response.data.success,
      message: response.data.message,
      tender: response.data.tender,
      engineer: response.data.engineer,
    })

    if (response.data.success) {
      const updatedTender = response.data.tender
      const engineer = response.data.engineer

      const assignedEngineerName =
        updatedTender?.assignedEngineerName || engineer?.name || assignmentData.engineerName

      notificationService.success(
        `Tender ${assignmentData.tenderId} assigned to engineer ${assignedEngineerName} successfully!`,
        4000,
      )

      const tenderIndex = tenders.value.findIndex((t) => t.t247Id === assignmentData.tenderId)
      if (tenderIndex !== -1) {
        const updatedTenderData: WonTenderResponse = {
          ...tenders.value[tenderIndex],
          assignedToEngineer: assignmentData.engineerId,
          assignedEngineerName: assignedEngineerName,
          engineerStatus: 'assigned',
          engineerAssignedAt: new Date().toISOString(),
          engineerNotes: assignmentData.notes,
          status: 'assigned-to-engineer',
          workflowStatus: 'assigned-to-engineer',
        }
        tenders.value[tenderIndex] = updatedTenderData
      }

      isEngineerAssignmentModalOpen.value = false
      selectedTenderForEngineerAssignment.value = null

      setTimeout(() => {
        fetchWonTenders()
      }, 500)
    } else {
      notificationService.error(
        `Failed to assign engineer: ${response.data.error || 'Unknown error'}`,
      )
    }
  } catch (err: unknown) {
    const axiosError = err as AxiosError
    console.error('❌ Error assigning engineer:', {
      error: err,
      response: axiosError.response?.data,
      status: axiosError.response?.status,
    })
    notificationService.error(`Failed to assign engineer: ${getAxiosErrorMessage(axiosError)}`)
  }
}

const handleCancelEngineerAssignment = () => {
  isEngineerAssignmentModalOpen.value = false
  selectedTenderForEngineerAssignment.value = null
}

// Export won tenders
// const exportWonTenders = async () => {
//   try {
//     notificationService.info('Exporting won tenders...')
//     const response = await api.get('/api/tenders/won/export', {
//       responseType: 'blob',
//     })

//     const url = window.URL.createObjectURL(new Blob([response.data]))
//     const link = document.createElement('a')
//     link.href = url
//     link.setAttribute('download', `won-tenders-${new Date().toISOString().split('T')[0]}.xlsx`)
//     document.body.appendChild(link)
//     link.click()
//     link.remove()

//     notificationService.success('Won tenders exported successfully!')
//   } catch (err: unknown) {
//     console.error('Error exporting won tenders:', err)
//     notificationService.error(`Failed to export: ${getAxiosErrorMessage(err as AxiosError)}`)
//   }
// }

// Tooltip management for description
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>(); // Changed from null to undefined
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

// Tooltip handlers
const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target; // This will be HTMLElement, not null
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined; // Changed from null to undefined
  tooltipText.value = '';
};

// Lifecycle
onMounted(() => {
  fetchWonTenders()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Won Tenders" />

    <!-- Stats Cards -->
    <!-- <div class="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <StatsCards title="Total Won Tenders" :value="wonCount" :icon="EyeIcon" />
      <StatsCards
        title="Total Winning Amount"
        :value="`₹${formatNumber(totalWinningAmount)}`"
        :icon="IndianRupeeIcon"
      />
    </div> -->

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button
            @click="refreshData"
            class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
          >
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Won Tenders Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex items-center justify-between gap-2">
          <!-- Search Input -->
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search won tenders..."
            id="searchwontenders"
            name="searchwontenders"
            type="text"
            size="xss"
          />

          <!-- Action Buttons -->
          <div class="flex gap-2 ml-auto">
            <!-- Clear Filter Button -->
            <BaseButton
              v-if="searchFilter"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />

            <!-- Export Button -->
            <!-- <BaseButton
              text="Export"
              size="sm"
              variant="outline"
              :icon="FileExportIcon"
              @click="exportWonTenders"
              :disabled="tenders.length === 0"
            /> -->

            <!-- Refresh Button -->
            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              @click="refreshData"
              :loading="loading"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          Loading won tender data...
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="w-full flex flex-col items-center text-center py-8 gap-3 text-red-500"
        >
          <p>{{ error }}</p>
          <BaseButton text="Retry" size="sm" @click="refreshData" class="w-30" />
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="GlobeIcon"
          title="No won tenders"
          description="No tenders have been marked as won yet"
          hint="Mark tenders as won from the Agent Tenders or Tender Details page"
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="GlobeIcon"
          title="No matching won tenders"
          description="No won tenders match your search criteria."
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <DataTable
          v-else-if="filteredTenders.length > 0"
          :value="filteredTenders"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="450px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
            thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
            tbody: { style: { position: 'relative', zIndex: '10' } },
          }"
        >
          <!-- Tender ID Column -->
          <Column
            sortable
            frozen
            field="referenceNo"
            header="Reference No"
            headerStyle="position: sticky; left: 48px; z-index: 30;"
            bodyStyle="position: sticky; left: 48px; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex flex-col max-w-[100px] text-xs">
                <a
                  :href="data.tenderBriefLink"
                  target="_blank"
                  class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                  v-if="data.tenderBriefLink"
                >
                  {{ data.referenceNo }}
                  <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                </a>
                <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
              </div>
            </template>
          </Column>

          <!-- Won Date Column -->
          <Column sortable field="wonAt" header="Won Date">
            <template #body="{ data }">
              <div class="flex flex-col text-xs">
                {{ formatDate(data.wonAt || '') }}
              </div>
            </template>
          </Column>

          <!-- Status Column - Using getWorkflowLabel and getBadgeStatus -->
          <Column sortable field="workflowStatus" header="Status">
            <template #body="{ data }">
              <Badge
                :value="getWorkflowLabel(data.workflowStatus || 'won')"
                :class="'p-badge-' + getBadgeStatus(data.workflowStatus || 'won')"
              />
            </template>
          </Column>

          <!-- Type Column -->
          <Column sortable field="type" header="Type">
            <template #body="{ data }">
              <Badge
                :value="data.sheetType"
                :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
              />
            </template>
          </Column>

          <!-- Tender Brief Column -->
          <Column sortable field="tenderBrief" header="Description">
            <template #body="{ data }">
              <div
                class="line-clamp-2 text-xs cursor-help"
                @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                @mouseleave="hideTooltip"
              >
                {{ data.tenderBrief }}
              </div>
            </template>
          </Column>

          <!-- Organization Column -->
          <Column sortable field="organization" header="Organization">
            <template #body="{ data }">
              <div class="text-xs">{{ data.organization }}</div>
            </template>
          </Column>

          <!-- Location Column -->
          <Column sortable field="location" header="Location">
            <template #body="{ data }">
              <span class="text-xs">{{ data.location }}</span>
            </template>
          </Column>

          <!-- Winning Amount Column -->
          <Column sortable field="winningAmount" header="Winning Amount">
            <template #body="{ data }">
              <div class="text-xs font-medium text-green-600">
                ₹{{ formatNumber(data.winningAmount || data.estimatedCost || 0) }}
              </div>
              <div v-if="data.winningAmount && data.estimatedCost" class="text-xs text-gray-500">
                <span v-if="data.winningAmount < data.estimatedCost" class="text-red-500">
                  ▼
                  {{
                    (
                      ((data.estimatedCost - data.winningAmount) / data.estimatedCost) *
                      100
                    ).toFixed(1)
                  }}% below estimate
                </span>
                <span v-else class="text-green-500">
                  ▲
                  {{
                    (
                      ((data.winningAmount - data.estimatedCost) / data.estimatedCost) *
                      100
                    ).toFixed(1)
                  }}% above estimate
                </span>
              </div>
            </template>
          </Column>

          <!-- Assigned Agent Column with Contact Info -->
          <Column sortable field="assignedAgent" header="Assigned Agent">
            <template #body="{ data }">
              <div class="flex flex-col space-y-2">
                <!-- Agent Name -->
                <div class="flex items-center justify-between">
                  <span class="text-xs font-medium text-gray-800">
                    {{ data.assignedAgentName || 'Unassigned' }}
                  </span>
                </div>

                <!-- Agent Contact Info -->
                <div
                  v-if="data.assignedAgentEmail || data.assignedAgentPhone"
                  class="space-y-1.5 text-xs"
                >
                  <!-- Email -->
                  <div
                    v-if="data.assignedAgentEmail && data.assignedAgentEmail !== 'Not available'"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <EnvelopeIcon class="w-4 h-4" />
                    <a
                      :href="`mailto:${data.assignedAgentEmail}`"
                      class="truncate hover:text-blue-600 hover:underline transition-colors"
                      :title="`Email: ${data.assignedAgentEmail}`"
                    >
                      {{ data.assignedAgentEmail }}
                    </a>
                  </div>

                  <!-- Phone -->
                  <div
                    v-if="data.assignedAgentPhone && data.assignedAgentPhone !== 'Not available'"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <MobileIcon class="w-4 h-4" />
                    <a
                      :href="`tel:${data.assignedAgentPhone}`"
                      class="truncate hover:text-green-600 hover:underline transition-colors"
                      :title="`Phone: ${data.assignedAgentPhone}`"
                    >
                      {{ data.assignedAgentPhone }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Assigned Engineer Column -->
          <!-- <Column sortable field="assignedEngineerName" header="Assigned Engineer">
            <template #body="{ data }">
              <div v-if="data.assignedEngineerName" class="flex flex-col space-y-1">
                <span class="text-sm font-medium text-blue-600">
                  {{ data.assignedEngineerName }}
                </span>
                <span v-if="data.engineerAssignedAt" class="text-xs text-gray-500">
                  Assigned: {{ formatDate(data.engineerAssignedAt) }}
                </span>
                <span v-if="data.engineerStatus" class="text-xs">
                  <Badge
                    :value="getWorkflowLabel(data.engineerStatus)"
                    :class="'p-badge-' + getBadgeStatus(data.engineerStatus)"
                  />
                </span>
                <span
                  v-if="data.engineerNotes"
                  class="text-xs text-gray-500 truncate"
                  :title="data.engineerNotes"
                >
                  Notes: {{ data.engineerNotes.substring(0, 30)
                  }}{{ data.engineerNotes.length > 30 ? '...' : '' }}
                </span>
              </div>
              <span v-else class="text-sm text-gray-400">Not Assigned</span>
            </template>
          </Column> -->

          <!-- Action Column -->
          <Column
            frozen
            field="action"
            header="Actions"
            headerStyle="width: 150px; position: sticky; right: 0; z-index: 30;"
            bodyStyle="position: sticky; right: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex gap-2">
                <!-- View button -->
                <BaseButton
                  size="xss"
                  :aria-label="`view ${data.t247Id} details`"
                  @click="viewTenderDetails(data)"
                  variant="secondary"
                  :icon="EyeIcon"
                  tooltip="View Tender Details"
                  tooltipPosition="bottom"
                />

                <!-- Engineer Assignment button -->
                <!-- <BaseButton
                  size="xs"
                  :aria-label="`Assign ${data.t247Id} to engineer`"
                  @click="openEngineerAssignmentModal(data)"
                  variant="secondary"
                  :icon="EngineerIcon"
                  tooltip="Assign To Engineer"
                  tooltipPosition="bottom"
                  :disabled="!!data.assignedEngineerName"
                /> -->
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} of {{ tenders.length }} won tenders
            <span v-if="searchFilter">(filtered)</span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span>Total value: ₹{{ formatNumber(totalWinningAmount) }}</span>
            <span class="ml-4">Last updated: {{ new Date().toLocaleTimeString() }}</span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <!-- Engineer Assignment Modal -->
  <ModalDefault
    v-model="isEngineerAssignmentModalOpen"
    title="Assign Won Tender to Engineer"
    width="max-w-lg"
    classBody="my-4"
  >
    <EngineerAssignmentModal
      ref="engineerAssignmentModalRef"
      v-if="selectedTenderForEngineerAssignment"
      :tender="selectedTenderForEngineerAssignment"
      @assign="handleEngineerAssignmentData"
      @cancel="handleCancelEngineerAssignment"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" @click="handleCancelEngineerAssignment" />
        <BaseButton text="Assign Tender" variant="primary" @click="handleEngineerAssignment" />
      </div>
    </template>
  </ModalDefault>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>

<style scoped>
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

.transition-colors {
  transition: color 0.2s ease;
}
</style>
